import { useState, useEffect } from 'react'
import { Car, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { StandardDialog } from '@/components/common/dialogs/StandardDialog'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'

interface CreateVehicleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (registrationNumber: string) => Promise<void>
  loading: boolean
  existingRegistrations?: string[]
}

export function CreateVehicleDialog({
  open,
  onOpenChange,
  onSubmit,
  loading,
  existingRegistrations = []
}: CreateVehicleDialogProps) {
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setRegistrationNumber('')
      setError(null)
    }
  }, [open])

  // Validate Swedish registration number format
  const validateRegistrationNumber = (regNumber: string): string | null => {
    const trimmed = regNumber.trim().toUpperCase()

    if (!trimmed) {
      return 'Registreringsnummer är obligatoriskt'
    }

    if (trimmed.length < 5 || trimmed.length > 7) {
      return 'Registreringsnummer måste vara 5-7 tecken'
    }

    // Swedish format: ABC 123 or ABC12Z (3 letters + 2-3 alphanumeric characters)
    const swedishPattern = /^[A-Z]{3}\s?[A-Z0-9]{2,3}$/
    if (!swedishPattern.test(trimmed)) {
      return 'Ogiltigt format. Använd svenskt format (ABC 123)'
    }

    // Check if already exists
    const normalizedExisting = existingRegistrations.map(reg =>
      reg.replace(/\s+/g, '').toUpperCase()
    )
    const normalizedInput = trimmed.replace(/\s+/g, '')

    if (normalizedExisting.includes(normalizedInput)) {
      return 'Detta registreringsnummer finns redan'
    }

    return null
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const validationError = validateRegistrationNumber(registrationNumber)
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      // Normalize format: ABC 123 (with space)
      const normalized = registrationNumber.trim().toUpperCase().replace(/\s+/g, ' ')
      await onSubmit(normalized)
    } catch (err) {
      // Error handling is done in parent component
      console.error('Error creating vehicle:', err)
    }
  }

  // Handle input change with real-time validation
  const handleInputChange = (value: string) => {
    setRegistrationNumber(value)

    // Clear error when user starts typing
    if (error) {
      setError(null)
    }
  }

  const dialogContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="registration" className="text-sm font-medium">
          Registreringsnummer *
        </Label>
        <Input
          id="registration"
          value={registrationNumber}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder="ABC 123"
          disabled={loading}
          className="text-center font-mono tracking-wider"
          autoComplete="off"
          autoFocus
        />
        <p className="text-xs text-muted-foreground">
          Ange registreringsnummer i svenskt format (ABC 123)
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </form>
  )

  const dialogFooter = (
    <div className="flex gap-3">
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={loading}
        className="flex-1"
      >
        Avbryt
      </Button>
      <Button
        onClick={handleSubmit}
        disabled={loading || !registrationNumber.trim()}
        className="flex-1"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <SpinnerIcon size="sm" />
            Skapar...
          </div>
        ) : (
          <>
            <Car className="w-4 h-4 mr-2" />
            Skapa Fordon
          </>
        )}
      </Button>
    </div>
  )

  return (
    <StandardDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Car className="w-5 h-5 text-blue-600" />
          </div>
          <span>Lägg till Nytt Fordon</span>
        </div>
      }
      description="Registrera ett nytt fordon i systemet genom att ange dess registreringsnummer."
      footer={dialogFooter}
      size="sm"
      scrollable={false}
    >
      {dialogContent}
    </StandardDialog>
  )
}